import {
    addDoc,
    collection,
    collectionGroup,
    deleteDoc,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc
} from 'firebase/firestore';
import { db } from './firebase';

const SUBSCRIPTIONS_COLLECTION = 'subscriptions';

/**
 * Subscribe to all subscriptions with real-time updates
 */
export const subscribeToSubscriptions = (callback) => {
  let usersMap = {};
  let subjectsDocs = [];

  const checkAndEmit = () => {
    const subscriptions = [];

    subjectsDocs.forEach((docSnap) => {
      const pathParts = docSnap.ref.path.split('/');
      if (pathParts[0] !== 'subscriptions') return; // Skip if it's from classes or other collections
      
      const parentDocRef = docSnap.ref.parent.parent;
      if (!parentDocRef) return;
      
      const userId = parentDocRef.id;
      const user = usersMap[userId] || {};
      const data = docSnap.data();
      const subjectId = docSnap.id;
      
      const isClassPackage = subjectId === '_class_package';
      
      let expiryDate = isClassPackage ? data.classPackageExpiresAt : data.expiresAt;
      let startDate = isClassPackage ? (data.lastRenewal || data.enrolledAt) : data.enrolledAt;
      
      let expiryDateObj = null;
      if (expiryDate) {
        expiryDateObj = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
      }
      
      let status = 'active';
      if (isClassPackage) {
        status = data.classPackageActive ? 'active' : 'expired';
      } else {
        status = data.status || 'active';
      }

      // Check if actually expired based on time
      const now = new Date();
      if (expiryDateObj && expiryDateObj < now && status === 'active') {
        status = 'expired';
      }

      // Format subject name
      let subjectName = '';
      if (isClassPackage) {
        subjectName = 'Full Class Package';
      } else {
        // E.g. chemistry_ss1 -> Chemistry (SS1)
        const parts = subjectId.split('_');
        if (parts.length >= 2) {
          const main = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
          const cls = parts[1].toUpperCase();
          subjectName = `${main} (${cls})`;
        } else {
          subjectName = subjectId.charAt(0).toUpperCase() + subjectId.slice(1);
        }
      }

      // Determine amount and plan
      let amount = data.amount;
      if (!amount) {
        if (isClassPackage) {
          amount = '₦10,000';
        } else {
          amount = '₦1,000';
        }
      }

      let planName = data.plan;
      if (!planName) {
        if (isClassPackage) {
          planName = 'Yearly Plan';
        } else {
          planName = data.tier === 'bundle' ? 'Bundle Plan' : 'Monthly Plan';
        }
      }

      subscriptions.push({
        id: `${userId}_${subjectId}`, // unique id
        userId: userId,
        subjectId: subjectId,
        student: user.fullName || 'Unknown Student',
        email: user.email || 'N/A',
        class: user.classLevel || 'N/A',
        subject: subjectName,
        plan: planName,
        amount: amount,
        startDate: startDate,
        expiryDate: expiryDate,
        status: status,
        createdAt: startDate || serverTimestamp(),
      });
    });

    // Sort by startDate / createdAt descending
    subscriptions.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
      return dateB - dateA;
    });

    callback(subscriptions);
  };

  // Listen to users
  const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
    usersMap = {};
    snapshot.docs.forEach((docSnap) => {
      usersMap[docSnap.id] = docSnap.data();
    });
    checkAndEmit();
  }, (err) => {
    console.warn('Subscription service: users listener error', err);
    checkAndEmit();
  });

  // Listen to collectionGroup subjects
  const unsubscribeSubjects = onSnapshot(collectionGroup(db, 'subjects'), (snapshot) => {
    subjectsDocs = snapshot.docs;
    checkAndEmit();
  }, (err) => {
    console.warn('Subscription service: subjects collectionGroup listener error', err);
    checkAndEmit();
  });

  // Return function to unsubscribe both
  return () => {
    unsubscribeUsers();
    unsubscribeSubjects();
  };
};

/**
 * Add a new subscription
 */
export const addSubscription = async (subscriptionData) => {
  try {
    const docRef = await addDoc(collection(db, SUBSCRIPTIONS_COLLECTION), {
      ...subscriptionData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding subscription:', error);
    throw error;
  }
};

/**
 * Update an existing subscription
 */
export const updateSubscription = async (subscriptionId, updates) => {
  try {
    const subscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, subscriptionId);
    await updateDoc(subscriptionRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

/**
 * Delete a subscription
 */
export const deleteSubscription = async (userId, subjectId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', userId, 'subjects', subjectId);
    await deleteDoc(subscriptionRef);
  } catch (error) {
    console.error('Error deleting subscription:', error);
    throw error;
  }
};

/**
 * Extend subscription expiry date
 */
export const extendSubscription = async (userId, subjectId, days) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', userId, 'subjects', subjectId);
    const subDoc = await getDoc(subscriptionRef);
    if (!subDoc.exists()) {
      throw new Error('Subscription not found');
    }
    const data = subDoc.data();
    
    const isClassPackage = subjectId === '_class_package';
    let currentExpiry;
    if (isClassPackage) {
      if (data.classPackageExpiresAt) {
        currentExpiry = data.classPackageExpiresAt.toDate ? data.classPackageExpiresAt.toDate() : new Date(data.classPackageExpiresAt);
      } else {
        currentExpiry = new Date();
      }
    } else {
      if (data.expiresAt) {
        currentExpiry = data.expiresAt.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt);
      } else {
        currentExpiry = new Date();
      }
    }
    
    // Add days
    const newExpiry = new Date(currentExpiry.getTime() + days * 24 * 60 * 60 * 1000);
    
    if (isClassPackage) {
      await updateDoc(subscriptionRef, {
        classPackageExpiresAt: newExpiry,
        classPackageActive: true,
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(subscriptionRef, {
        expiresAt: newExpiry,
        status: 'active',
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error extending subscription:', error);
    throw error;
  }
};

/**
 * Renew an expired or expiring subscription
 */
export const renewSubscription = async (userId, subjectId, plan, amount, days) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', userId, 'subjects', subjectId);
    const newStart = new Date();
    const newExpiry = new Date(newStart.getTime() + days * 24 * 60 * 60 * 1000);
    
    const formattedAmount = typeof amount === 'string' && amount.startsWith('₦') 
      ? amount 
      : `₦${Number(amount).toLocaleString('en-NG')}`;

    const isClassPackage = subjectId === '_class_package';
    if (isClassPackage) {
      await updateDoc(subscriptionRef, {
        classPackageActive: true,
        classPackageExpiresAt: newExpiry,
        lastRenewal: newStart,
        tier: 'class',
        plan: plan,
        amount: formattedAmount,
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(subscriptionRef, {
        status: 'active',
        enrolledAt: newStart,
        expiresAt: newExpiry,
        plan: plan,
        amount: formattedAmount,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error renewing subscription:', error);
    throw error;
  }
};

/**
 * Calculate subscription statistics
 */
export const calculateSubscriptionStats = (subscriptions) => {
  const now = new Date();
  
  const active = subscriptions.filter(s => s.status === 'active').length;
  const expired = subscriptions.filter(s => s.status === 'expired').length;
  const expiring = subscriptions.filter(s => {
    if (s.status !== 'active') return false;
    const expiryDate = s.expiryDate?.toDate?.() || new Date(s.expiryDate);
    const daysUntilExpiry = Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
  }).length;
  
  const cancelled = subscriptions.filter(s => s.status === 'cancelled').length;
  
  // Calculate total revenue
  const totalRevenue = subscriptions
    .filter(s => s.status === 'active' || s.status === 'expired')
    .reduce((sum, s) => {
      const amount = parseFloat(s.amount?.replace(/[^0-9.-]+/g, '') || 0);
      return sum + amount;
    }, 0);
  
  return {
    total: subscriptions.length,
    active,
    expired,
    expiring,
    cancelled,
    totalRevenue,
  };
};
